import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../app/store"
import { Category } from "../models/category.model"

export type PaginationType = {
  page: number
  pageSize: number
}

export type CategoryFilterType = {
  search: string | null
  category1?: string
  orderBy?: string
  sortBy?: string
  source?: string
  category2?: string
}

type CategoryState = {
  categoryList: Category[]
  isCategoryLoading: boolean
  isCategoryFormOpen: boolean | Category
  pageInfo: {
    totalRowCount: number
  }
  pagination: PaginationType
  filters: CategoryFilterType
  filterCount: number
  filtersReset: boolean
}

export type CategoriesState = {
  Category1: CategoryState
  Category2: CategoryState
  Category3: CategoryState
}

export const initialPaginationState = {
  page: 0,
  pageSize: 10,
}

export const initialFilterState = () => ({
  category1: "",
  orderBy: "desc",
  search: null,
  sortBy: "date",
  source: "",
  category2: "",
})

export const categoryInitialState: CategoryState = {
  categoryList: [],
  isCategoryLoading: false,
  isCategoryFormOpen: false,
  pagination: initialPaginationState,
  pageInfo: {
    totalRowCount: 0,
  },
  filters: initialFilterState(),
  filterCount: 0,
  filtersReset: false,
}

export const categoriesInitialState: CategoriesState = {
  Category1: { ...categoryInitialState },
  Category2: { ...categoryInitialState },
  Category3: { ...categoryInitialState },
}

export type fetchCategoryListPayloadType = {
  category: keyof CategoriesState
  page: number
  pageSize: number
  search: string | null
  sortBy: string
  orderBy: string
  level: string
  category1?: string
  category2?: string
  source?: string
}

export type addCategoryPayloadType = {
  label: string
  level: string
  category: keyof CategoriesState
  source?: string
  category1?: string
  category2?: string
}

export type updateCategoryPayloadType = {
  id: string
  label: string
  level: string
  category: keyof CategoriesState
  source?: string
  category1?: string
  category2?: string
  initialCategory2?: string
}

export const categorySlice = createSlice({
  name: "category",
  initialState: categoriesInitialState,
  reducers: {
    fetchCategoryList: (
      state,
      action: PayloadAction<fetchCategoryListPayloadType>,
    ) => {
      const { category } = action.payload
      state[category].categoryList = []
      state[category].isCategoryLoading = true
    },

    fetchCategoryListSuccess: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
        categoryList: Category[]
        totalRowCount: number
      }>,
    ) => {
      const { category, categoryList, totalRowCount } = action.payload
      state[category].categoryList = categoryList
      state[category].pageInfo.totalRowCount = totalRowCount
      state[category].isCategoryLoading = false
    },
    fetchCategoryListFailed: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = false
    },

    addCategory: (state, action: PayloadAction<addCategoryPayloadType>) => {
      const { category } = action.payload
      state[category].isCategoryLoading = true
    },

    addCategorySuccess: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = false
      state[category].isCategoryFormOpen = false
    },
    addCategoryFailed: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = false
    },

    updateCategory: (
      state,
      action: PayloadAction<updateCategoryPayloadType>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = true
    },
    updateCategorySuccess: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = false
      state[category].isCategoryFormOpen = false
    },
    updateCategoryFailed: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryLoading = false
    },

    setCategoryFilters: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
        filters: CategoryFilterType
      }>,
    ) => {
      const { category, filters } = action.payload
      state[category].filters = filters
      state[category].pagination.page = 0
    },
    setCategoryFiltersFromURL: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
        filters: CategoryFilterType
      }>,
    ) => {
      const { category, filters } = action.payload
      state[category].filters = filters
    },
    resetCategoryFilters: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].filters = initialFilterState()
      state[category].pagination = initialPaginationState
      state[category].filtersReset = true
    },
    clearFiltersResetFlag: (
      state,
      action: PayloadAction<{ category: keyof CategoriesState }>,
    ) => {
      const { category } = action.payload
      state[category].filtersReset = false
    },
    setCategoryFormOpen: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
        row?: boolean | Category
      }>,
    ) => {
      const { category, row } = action.payload
      state[category].isCategoryFormOpen = row || true
    },
    setCategoryFormClose: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
      }>,
    ) => {
      const { category } = action.payload
      state[category].isCategoryFormOpen = false
    },

    setCategoryPagination: (
      state,
      action: PayloadAction<{
        category: keyof CategoriesState
        pagination: PaginationType
      }>,
    ) => {
      const { category, pagination } = action.payload
      state[category].pagination = pagination
    },
    setFilterCount: (
      state,
      { payload }: { payload: { category: keyof CategoriesState } },
    ) => {
      const filters = state[payload.category].filters
      const initialFilters = initialFilterState()

      const filterKeys = [
        "dateSelector",
        "accountId",
        "transactionType",
        "paymentMethod",
        "operator",
        "isMarked",
        "entityType",
      ]

      const count = Object.keys(filters).filter((key) => {
        return (
          filterKeys.includes(key) &&
          filters[key as keyof typeof filters] !==
            initialFilters[key as keyof typeof filters]
        )
      }).length

      state[payload.category].filterCount = count
    },
  },
})

export const {
  fetchCategoryList,
  fetchCategoryListFailed,
  fetchCategoryListSuccess,
  addCategory,
  addCategorySuccess,
  addCategoryFailed,
  updateCategory,
  updateCategorySuccess,
  updateCategoryFailed,
  setCategoryFilters,
  setCategoryFiltersFromURL,
  setCategoryPagination,
  resetCategoryFilters,
  clearFiltersResetFlag,
  setCategoryFormOpen,
  setCategoryFormClose,
} = categorySlice.actions

export const selectCategories = (state: RootState) => state.category

export default categorySlice.reducer
